import { useRef, useState } from "react";
import { logout } from "../../firebase/auth";
import { deleteSession, renameSession } from "../../firebase/firestore";
import {
  TrashIcon,
  XMarkIcon,
  PencilIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { useSubscription } from "../../context/SubscriptionContext";
import Logo from "../common/Logo";
import Modal from "../common/Modal";

const MAX_SESSION_NAME_LENGTH = 80;

export default function Sidebar({
  user,
  sessions,
  activeSession,
  setActiveSession,
  setSessions,
  mobileOpen = false,
  onClose = () => {},
  view = "dashboard",
  setView = () => {},
}) {

  const { plan, usage, capabilities, remaining } = useSubscription();

  // ----------------------------------
  // SESSION RENAME
  // ----------------------------------

  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const renameInputRef = useRef(null);

  const startRename = (e, s) => {
    e.stopPropagation();
    setRenamingId(s.firebaseId);
    setRenameValue(s.name || "");
  };

  const cancelRename = (e) => {
    e?.stopPropagation();
    setRenamingId(null);
    setRenameValue("");
  };

  const saveRename = async (e, sessionId) => {
    e?.stopPropagation();

    const trimmed = renameValue.trim().slice(0, MAX_SESSION_NAME_LENGTH);
    const original = sessions.find((s) => s.firebaseId === sessionId)?.name || "";

    // Empty or whitespace-only names, or a name identical to the current
    // one, just cancel the edit instead of writing to Firestore.
    if (!trimmed || trimmed === original) {
      setRenamingId((current) => (current === sessionId ? null : current));
      setRenameValue("");
      return;
    }

    setRenameBusy(true);

    try {
      await renameSession(sessionId, trimmed);

      setSessions((prev) =>
        prev.map((s) =>
          s.firebaseId === sessionId ? { ...s, name: trimmed } : s
        )
      );

      // Keep the currently selected session in sync so the header/chat
      // view reflects the new name immediately without losing selection.
      if (activeSession?.firebaseId === sessionId) {
        setActiveSession((prev) =>
          prev && prev.firebaseId === sessionId
            ? { ...prev, name: trimmed }
            : prev
        );
      }

      // Only clear the "currently renaming" row if the user hasn't already
      // moved on to renaming a different session while this write was
      // in flight — otherwise we'd wipe out their next edit.
      setRenamingId((current) => (current === sessionId ? null : current));
      setRenameValue("");
    } catch (err) {
      console.error("Failed to rename session:", err);
    } finally {
      setRenameBusy(false);
    }
  };

  const handleRenameKeyDown = (e, sessionId) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveRename(e, sessionId);
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelRename(e);
    }
  };

  // ----------------------------------
  // NEW CHAT
  // ----------------------------------

  const newChat = () => {
    // No empty session anymore.
    // Dashboard will automatically show FileUpload.
    setActiveSession(null);
    onClose();
  };

  const selectSession = (s) => {
    setActiveSession(s);
    onClose();
  };

  // ----------------------------------
  // DELETE SESSION
  // ----------------------------------

  const [deleteTarget, setDeleteTarget] = useState(null); // session being confirmed for deletion
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const requestDelete = (e, s) => {
    e.stopPropagation();
    setDeleteError(null);
    setDeleteTarget(s);
  };

  const closeDeleteDialog = () => {
    if (deleteBusy) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleteBusy(true);
    setDeleteError(null);

    try {
      await deleteSession(deleteTarget.firebaseId);

      const updated = sessions.filter(
        (s) => s.firebaseId !== deleteTarget.firebaseId
      );

      setSessions(updated);

      if (activeSession?.firebaseId === deleteTarget.firebaseId) {
        setActiveSession(null);
      }

      setDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete dataset:", err);
      setDeleteError("Failed to delete this dataset. Please try again.");
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          onClick={onClose}
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
        />
      )}

      <div
        className={`
          fixed inset-y-0 left-0 z-50 w-[82%] max-w-72
          lg:static lg:z-auto lg:w-72 lg:max-w-none
          bg-gray-900 border-r border-gray-800 flex flex-col
          transition-transform duration-300 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
        `}
      >

        {/* Brand */}
        <div className="px-4 pt-4 flex items-center gap-2.5">
          <Logo size={28} />
          <span className="font-bold text-white tracking-tight truncate">
            InsightPulse AI
          </span>
        </div>

        {/* Header */}
        <div className="p-4 flex items-center gap-2">

          <button
            onClick={newChat}
            className="flex-1 bg-blue-600 hover:bg-blue-700 transition rounded-xl py-3 font-semibold"
          >
            + New Dataset
          </button>

          <button
            onClick={onClose}
            className="lg:hidden w-11 h-11 shrink-0 rounded-xl bg-gray-800 hover:bg-gray-700 flex items-center justify-center"
            aria-label="Close menu"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

        </div>

        {/* Sessions */}
        <div className="flex-1 overflow-y-auto px-3 pb-3">

          {sessions.length === 0 && (
            <div className="text-center text-gray-500 mt-10 text-sm">
              No datasets yet.
            </div>
          )}

          {sessions.map((s) => {

            const selected =
              activeSession?.firebaseId === s.firebaseId;

            const isRenaming = renamingId === s.firebaseId;

            return (

              <div
                key={s.firebaseId}
                onClick={() => !isRenaming && selectSession(s)}
                className={`
                  cursor-pointer
                  rounded-xl
                  p-3
                  mb-2
                  transition-all
                  border
                  relative

                  ${
                    selected
                      ? "bg-blue-600 border-blue-500 shadow-lg"
                      : "bg-gray-800 border-gray-700 hover:bg-gray-700"
                  }
                `}
              >

                {!isRenaming && (
                  <div className="absolute top-2 right-2 flex items-center gap-1">
                    {/* Rename Button */}
                    <button
                      onClick={(e) => startRename(e, s)}
                      aria-label="Rename dataset"
                      className="
                        p-1.5
                        rounded-lg
                        text-gray-400
                        hover:text-white
                        hover:bg-white/10
                        transition-all
                      "
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>

                    {/* Delete Button */}
                    <button
                      onClick={(e) => requestDelete(e, s)}
                      aria-label="Delete dataset"
                      className="
                        p-1.5
                        rounded-lg
                        text-gray-400
                        hover:text-red-500
                        hover:bg-red-500/10
                        transition-all
                      "
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {isRenaming ? (
                  <div
                    className="flex items-center gap-1.5 pr-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      ref={renameInputRef}
                      autoFocus
                      type="text"
                      value={renameValue}
                      maxLength={MAX_SESSION_NAME_LENGTH}
                      disabled={renameBusy}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => handleRenameKeyDown(e, s.firebaseId)}
                      onBlur={(e) => saveRename(e, s.firebaseId)}
                      className="
                        min-w-0 flex-1
                        bg-gray-900
                        border border-gray-600
                        focus:border-blue-500
                        rounded-lg
                        px-2 py-1.5
                        text-sm
                        text-white
                        outline-none
                        disabled:opacity-60
                      "
                    />

                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => saveRename(e, s.firebaseId)}
                      disabled={renameBusy}
                      aria-label="Save name"
                      className="p-1.5 rounded-lg text-emerald-400 hover:bg-emerald-500/10 transition-all shrink-0 disabled:opacity-50"
                    >
                      <CheckIcon className="w-4 h-4" />
                    </button>

                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={cancelRename}
                      disabled={renameBusy}
                      aria-label="Cancel rename"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all shrink-0 disabled:opacity-50"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="font-medium truncate pr-16">
                    📊 {s.name}
                  </div>
                )}

                {!isRenaming && (
                  <div className="text-xs text-gray-300 mt-2">
                    {s.analysis?.rows ?? 0} rows
                    {" • "}
                    {s.analysis?.columnCount ?? 0} columns
                  </div>
                )}

              </div>

            );

          })}

        </div>

        {/* Plan & Usage */}
        <div className="border-t border-gray-800 p-4 space-y-2">

          <button
            onClick={() => { setView(view === "reports" ? "dashboard" : "reports"); onClose(); }}
            className={`w-full text-left rounded-xl px-3 py-2.5 border transition flex items-center justify-between ${
              view === "reports"
                ? "bg-blue-600 border-blue-500"
                : "bg-gray-800 border-gray-700 hover:bg-gray-750"
            }`}
          >
            <span className="text-sm font-medium flex items-center gap-1.5">
              📬 Scheduled Reports
            </span>
            {!capabilities.canScheduleReports && (
              <span className="text-[10px] bg-white/10 px-1.5 py-0.5 rounded-full font-semibold text-gray-300">PRO</span>
            )}
          </button>

          <button
            onClick={() => { setView(view === "subscription" ? "dashboard" : "subscription"); onClose(); }}
            className={`w-full text-left rounded-xl p-3 border transition ${
              view === "subscription"
                ? "bg-blue-600 border-blue-500"
                : "bg-gray-800 border-gray-700 hover:bg-gray-750"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium flex items-center gap-1.5">
                {plan === "premium" && <span className="text-[10px] bg-blue-500/80 px-1.5 py-0.5 rounded-full font-semibold">PRO</span>}
                {capabilities.name} Plan
              </span>
              <span className="text-xs text-gray-300">Manage</span>
            </div>

            <div className="text-xs text-gray-300 mt-1.5">
              {remaining} of {capabilities.monthlyAnalysisLimit} analyses remaining
            </div>

            <div className="mt-2 h-1.5 bg-black/30 rounded-full overflow-hidden">
              <div
                className={`h-full ${remaining === 0 ? "bg-red-400" : "bg-white/70"}`}
                style={{ width: `${Math.min((usage.count / capabilities.monthlyAnalysisLimit) * 100, 100)}%` }}
              />
            </div>
          </button>

        </div>

        {/* User */}
        <div className="border-t border-gray-800 p-4">

          <div className="flex items-center gap-3 mb-4">

            {user?.photoURL ? (

              <img
                src={user.photoURL}
                alt="Profile"
                className="w-11 h-11 rounded-full shrink-0"
              />

            ) : (

              <div className="w-11 h-11 rounded-full bg-gray-700 flex items-center justify-center shrink-0">
                👤
              </div>

            )}

            <div className="overflow-hidden">

              <div className="font-medium truncate">
                {user?.displayName || "User"}
              </div>

              <div className="text-xs text-gray-400 truncate">
                {user?.email}
              </div>

            </div>

          </div>

          <button
            onClick={logout}
            className="w-full bg-red-600 hover:bg-red-700 rounded-xl py-2 transition"
          >
            Logout
          </button>

          <div className="flex items-center justify-center gap-x-3 gap-y-1 flex-wrap mt-4 text-[11px] text-gray-500">
            <button onClick={() => { setView("privacy"); onClose(); }} className="hover:text-gray-300 transition">
              Privacy Policy
            </button>
            <span className="text-gray-700">•</span>
            <button onClick={() => { setView("terms"); onClose(); }} className="hover:text-gray-300 transition">
              Terms & Conditions
            </button>
            <span className="text-gray-700">•</span>
            <button onClick={() => { setView("refund-policy"); onClose(); }} className="hover:text-gray-300 transition">
              Refund Policy
            </button>
            <span className="text-gray-700">•</span>
            <button onClick={() => { setView("feedback"); onClose(); }} className="hover:text-gray-300 transition">
              Feedback
            </button>
          </div>

        </div>

      </div>

      {/* DELETE CONFIRMATION */}
      <Modal
        open={!!deleteTarget}
        onClose={closeDeleteDialog}
        dismissible={!deleteBusy}
        title="Delete this dataset?"
        footer={
          <>
            <button
              onClick={closeDeleteDialog}
              disabled={deleteBusy}
              className="px-4 py-2.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 transition text-sm font-medium disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              onClick={confirmDelete}
              disabled={deleteBusy}
              className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 transition text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {deleteBusy && (
                <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              )}
              {deleteBusy ? "Deleting..." : "Delete Permanently"}
            </button>
          </>
        }
      >
        <p>
          <span className="text-white font-medium">
            {deleteTarget?.name}
          </span>{" "}
          and its chat history will be permanently deleted. This can't be undone.
        </p>

        {deleteError && (
          <p className="mt-3 text-red-400 text-xs">{deleteError}</p>
        )}
      </Modal>
    </>
  );
}
